// Feature 009: Deal Operating System
// Persistent AI Assistant Context with localStorage (user-scoped)
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Get storage key scoped by user ID
const getStorageKey = (userId) => `usokie_assistant_state_${userId || 'anonymous'}`;

const AssistantContext = createContext(null);

export const useAssistant = () => {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistant must be used within AssistantProvider');
  }
  return context;
};

// Load state from localStorage (user-scoped)
const loadPersistedState = (userId) => {
  try {
    const storageKey = getStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert timestamp strings back to Date objects
      if (parsed.messages) {
        parsed.messages = parsed.messages.map(m => ({
          ...m,
          timestamp: new Date(m.timestamp)
        }));
      }
      return parsed;
    }
  } catch (e) {
    console.warn('Failed to load assistant state:', e);
  }
  return null;
};

// Save state to localStorage (user-scoped)
const persistState = (userId, state) => {
  try {
    const storageKey = getStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to persist assistant state:', e);
  }
};

// Clear old anonymous state on login
const clearAnonymousState = () => {
  try {
    localStorage.removeItem(getStorageKey('anonymous'));
  } catch (e) {
    // Ignore
  }
};

export const AssistantProvider = ({ children, csrfToken, userId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [mode, setMode] = useState('new'); // 'new' or 'enrich'
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [pendingMatch, setPendingMatch] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Load persisted state on mount (user-scoped)
  useEffect(() => {
    // Clear any anonymous state when user logs in
    if (userId && userId !== 'anonymous') {
      clearAnonymousState();
    }

    const persisted = loadPersistedState(userId);
    if (persisted) {
      setIsOpen(persisted.isOpen || false);
      setMessages(persisted.messages || []);
      setMode(persisted.mode || 'new');
      setSelectedOpportunity(persisted.selectedOpportunity || null);
      // Don't restore extractedData or pendingMatch - they're transient
    }
    setInitialized(true);
  }, [userId]);

  // Persist state on changes (user-scoped)
  useEffect(() => {
    // Only persist after initial load
    if (!initialized) return;

    const timeoutId = setTimeout(() => {
      persistState(userId, {
        isOpen,
        messages: messages.slice(-50), // Keep last 50 messages
        mode,
        selectedOpportunity,
      });
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [userId, isOpen, messages, mode, selectedOpportunity, initialized]);

  const toggleAssistant = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const openAssistant = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeAssistant = useCallback(() => {
    setIsOpen(false);
  }, []);

  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, {
      ...message,
      timestamp: message.timestamp || new Date(),
    }]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setMode('new');
    setSelectedOpportunity(null);
    setExtractedData(null);
    setPendingMatch(null);
  }, []);

  const selectOpportunity = useCallback((opportunity) => {
    setSelectedOpportunity(opportunity);
    setMode('enrich');
  }, []);

  const clearEnrichment = useCallback(() => {
    setSelectedOpportunity(null);
    setMode('new');
  }, []);

  const value = {
    // State
    isOpen,
    messages,
    mode,
    selectedOpportunity,
    extractedData,
    pendingMatch,
    csrfToken,

    // Setters
    setMessages,
    setMode,
    setSelectedOpportunity,
    setExtractedData,
    setPendingMatch,

    // Actions
    toggleAssistant,
    openAssistant,
    closeAssistant,
    addMessage,
    clearMessages,
    selectOpportunity,
    clearEnrichment,
  };

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};

export default AssistantContext;
